import React from 'react'
import PropTypes from 'prop-types'
import { Button, Modal, Tag } from 'antd'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import {
  clearGroupCreateGate,
  postCreateGroupRequest
} from '../../actions/groupActions'
import { postAccessRequestRequest } from '../../actions/medicialGroupActions'
import { ALEM_HEALTH_EMAIL_SUPPORT } from '../../config/constants'
import {
  getBlockedOrganization,
  maskEmailAddress
} from '../../utils/group-dedupe'

function getDomainOrganization (domain = {}) {
  const groupId = domain.organization_guid || domain.group_id
  if (!groupId) return null

  return {
    groupId,
    groupName: domain.organization_name || domain.name || ''
  }
}

export function GroupCreationGate ({
  clearGroupCreateGate,
  groupCreateGate,
  organizationDomain,
  postAccessRequestRequest,
  postCreateGroupRequest
}) {
  const { t } = useTranslation()
  const {
    pendingPayload,
    responseJSON,
    similarGroups = [],
    status
  } = groupCreateGate
  const isSimilarGroupsGate = status === 'similar_groups'
  const isDomainBlocked = status === 'domain_blocked'
  const blockedOrganization = getBlockedOrganization(responseJSON) ||
    getDomainOrganization(organizationDomain?.data)
  const isDomainLookupPending = isDomainBlocked &&
    (organizationDomain?.status === 'idle' || organizationDomain?.status === 'loading')

  const requestAccess = (groupId, groupName) => {
    if (!groupId) return

    clearGroupCreateGate()
    // Reuse the established request-access saga so user metadata, Zapier
    // reporting, notifications, and success routing remain consistent.
    postAccessRequestRequest({
      organization_guid: groupId,
      organization_name: groupName
    })
  }

  const confirmCreate = () => {
    if (!pendingPayload) return

    clearGroupCreateGate()
    postCreateGroupRequest({
      ...pendingPayload,
      confirm_create: true
    })
  }

  const renderSimilarGroups = () => (
    <div className='group-creation-gate__list'>
      {similarGroups.map((group, index) => {
        const maskedEmail = maskEmailAddress(group.email_address)
        const isExactMatch = group.match_type === 'exact_name'

        return (
          <div
            className={`group-creation-gate__group${isExactMatch ? ' group-creation-gate__group--exact' : ''}`}
            key={group.group_id || `${group.name}-${index}`}
          >
            <div className='group-creation-gate__group-details'>
              <div className='group-creation-gate__group-heading'>
                <strong>{group.name}</strong>
                {isExactMatch && (
                  <Tag color='blue'>{t('group_dedupe.exact_match')}</Tag>
                )}
              </div>
              {maskedEmail && (
                <span className='group-creation-gate__email'>{maskedEmail}</span>
              )}
            </div>
            <Button
              disabled={!group.group_id}
              onClick={() => requestAccess(group.group_id, group.name)}
              type='primary'
            >
              {t('group_dedupe.join')}
            </Button>
          </div>
        )
      })}
    </div>
  )

  const renderDomainBlock = () => (
    <div className='group-creation-gate__domain-block'>
      <p>{t('group_dedupe.domain_block_description')}</p>
      {blockedOrganization?.groupName && (
        <strong className='group-creation-gate__blocked-name'>
          {blockedOrganization.groupName}
        </strong>
      )}
      {!blockedOrganization && !isDomainLookupPending && (
        <p>{t('group_dedupe.contact_admin')}</p>
      )}
    </div>
  )

  const footer = isSimilarGroupsGate
    ? [
        <Button key='create' onClick={confirmCreate}>
          {t('group_dedupe.create_anyway')}
        </Button>
      ]
    : [
        blockedOrganization
          ? (
            <Button
              key='request'
              onClick={() => requestAccess(
                blockedOrganization.groupId,
                blockedOrganization.groupName
              )}
              type='primary'
            >
              {t('group_dedupe.request_access')}
            </Button>
            )
          : (
            <Button
              href={ALEM_HEALTH_EMAIL_SUPPORT}
              key='support'
              loading={isDomainLookupPending}
              type='primary'
            >
              {isDomainLookupPending
                ? t('group_dedupe.finding_organization')
                : t('group_dedupe.contact_support')}
            </Button>
            )
      ]

  return (
    <Modal
      className='group-creation-gate'
      closable={false}
      footer={footer}
      keyboard={false}
      maskClosable={false}
      title={isSimilarGroupsGate
        ? t('group_dedupe.similar_title')
        : t('group_dedupe.domain_block_title')}
      visible={isSimilarGroupsGate || isDomainBlocked}
      width={640}
    >
      {isSimilarGroupsGate
        ? (
          <>
            <p>{t('group_dedupe.similar_description')}</p>
            {renderSimilarGroups()}
          </>
          )
        : renderDomainBlock()}
    </Modal>
  )
}

GroupCreationGate.propTypes = {
  clearGroupCreateGate: PropTypes.func.isRequired,
  groupCreateGate: PropTypes.shape({
    pendingPayload: PropTypes.object,
    responseJSON: PropTypes.object,
    similarGroups: PropTypes.array,
    status: PropTypes.string
  }).isRequired,
  organizationDomain: PropTypes.shape({
    data: PropTypes.object,
    status: PropTypes.string
  }).isRequired,
  postAccessRequestRequest: PropTypes.func.isRequired,
  postCreateGroupRequest: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  groupCreateGate: state.groupCreateGate,
  organizationDomain: state.organizationDomain
})

const mapDispatchToProps = dispatch => bindActionCreators({
  clearGroupCreateGate,
  postAccessRequestRequest,
  postCreateGroupRequest
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(GroupCreationGate)
